import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { styled } from 'styled-components';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import { Box } from '#/components/Box/Box';
import { MultiStepForm } from '#/components/Form/MultiStepForm/MultiStepForm';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { LoadingDots } from '#/components/LoadingDots/LoadingDots';
import { LoggedInSwitcher } from '#/components/LoggedInSwitcher/LoggedInSwitcher';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { H4 } from '#/components/Text/H4';
import { Text } from '#/components/Text/Text';
import { EmailSignupFormContainer } from '#/containers/EmailSignupFormContainer';
import { LoginFormContainer } from '#/containers/LoginFormContainer';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salariesGetCompensationSurvey } from '#/modules/salaries/api/salariesGetCompensationSurvey';
import { CareerPathChart } from '#/modules/salaries/components/CareerPathChart/CareerPathChart';
import { CareerPathChartHeader } from '#/modules/salaries/components/CareerPathChartHeader/CareerPathChartHeader';
import { CareerPathHeader } from '#/modules/salaries/components/CareerPathHeader/CareerPathHeader';
import { FeedbackBar } from '#/modules/salaries/components/FeedbackBar/FeedbackBar';
import { MainChart } from '#/modules/salaries/components/MainChart/MainChart';
import { MainChartFooter } from '#/modules/salaries/components/MainChartFooter/MainChartFooter';
import { MainChartHeader } from '#/modules/salaries/components/MainChartHeader/MainChartHeader';
import { MainChartSidebar } from '#/modules/salaries/components/MainChartSidebar/MainChartSidebar';
import { RecentSubmissions } from '#/modules/salaries/components/RecentSubmissions/RecentSubmissions';
import { RelatedJobs } from '#/modules/salaries/components/RelatedJobs/RelatedJobs';
import { ReportSection } from '#/modules/salaries/components/ReportSection/ReportSection';
import { buildCareerPathChart } from '#/modules/salaries/helpers/buildCareerPathChart';
import { buildMainChart } from '#/modules/salaries/helpers/buildMainChart';
import { salariesFaqRoute } from '#/modules/salaries/routing/salaries.routes';
import type { CalculatorReport } from '#/modules/salaries/types/CalculatorReport';
import type { EstimatedRange } from '#/modules/salaries/types/EstimatedRange';
import type { MainChartData } from '#/modules/salaries/types/MainChartData';
import type { SalariesReportFormData } from '#/modules/salaries/types/SalariesReportFormData';
import { DEFAULT_CHART_DATA } from '#/modules/salaries/utils/constants/defaultChartData';
import { breakpointValues, cssBreakpoints } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { scrollIntoView } from '#/utils/scrollIntoView';

function Loading() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight={300}
    >
      <LoadingDots />
    </Box>
  );
}

const NoDataContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  margin-bottom: 35px;
  text-align: center;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    justify-content: center;
  }
`;

type LocationState = {
  calculatorResult: CalculatorReport;
  initialValues: SalariesReportFormData;
};

function SalaryReportContainer() {
  const { i18n, locale } = useI18n();

  const location = useLocation<LocationState>();
  const [data, setData] = useState<MainChartData>();
  const [careerPathData, setCareerPathData] = useState<EstimatedRange[]>([]);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [salaryModelCreated, setSalaryModelCreated] = useState<number>();

  const [searchApiKey, setSearchApiKey] = useState<string>();
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [surveyLoading, setSurveyLoading] = useState(true);

  const INITIAL_MAIN_CHART = useMemo(
    () => buildMainChart(i18n, locale, DEFAULT_CHART_DATA),
    [i18n, locale],
  );

  const mainChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
      setSearchApiKey(newApiKey);
    });
  }, []);

  useEffect(() => {
    salariesGetCompensationSurvey().then(
      ({
        salaryModelCreated: newSalaryModelCreated,
        alreadySubmitted: newAlreadySubmitted,
      }) => {
        setAlreadySubmitted(newAlreadySubmitted);
        setSalaryModelCreated(Date.parse(newSalaryModelCreated) / 1000);
        setSurveyLoading(false);
      },
    );
  }, []);

  const scrollToChart = () => {
    if (mainChartRef.current && window.innerWidth < breakpointValues.md) {
      scrollIntoView(mainChartRef.current);
    }
  };

  const setResults = useCallback(
    (calculatorResult: CalculatorReport, values: SalariesReportFormData) => {
      const { submissionData, estimatedRange } = calculatorResult;

      if (submissionData || estimatedRange.scatterData) {
        const { filtersUsed = {} } = submissionData || {};
        const { xTicks, lineChart, barChart } = buildMainChart(
          i18n,
          locale,
          calculatorResult,
        );
        setData({
          // @ts-expect-error TS(2322): Type 'BarChart | null' is not assignable to type '... Remove this comment to see the full error message
          barChart,
          filtersUsed,
          xTicks,
          // @ts-expect-error TS(2322): Type 'LineChart | null' is not assignable to type ... Remove this comment to see the full error message
          lineChart,
          submittedData: {
            selectedSynonymFamilyId: values.selectedSynonymFamilyId,
            selectedSynonymId: values.selectedSynonymId,
            selectedSynonymLevel: values.selectedSynonymLevel,
            jobDescription: values.jobDescription,
            jobTitleSelected: values.jobTitle,
            jobTitleId: values.jobTitleId,
            city: values.city,
            geoType: values.geoType,
            stateCode: values.stateCode,
            latitude: values.latitude,
            longitude: values.longitude,
            yearsOfExperience: values.yearsOfExperience,
          },
        });
        trackEvent('Salary Calculator Report Displayed', {
          job_title: values.jobTitle,
          city: values.city,
          years_of_experience: values.yearsOfExperience,
          state: values.stateCode,
          user_id: undefined,
          employer_city_bucket: filtersUsed.cityBucket,
          sources: filtersUsed.sources,
          years_of_experience_used: filtersUsed.yearsOfExperience,
        });
      } else {
        // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        setData(null);
      }

      scrollToChart();
    },
    [i18n, locale],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (location.state && location.state.calculatorResult) {
      setResults(location.state.calculatorResult, location.state.initialValues);
    }
  }, [location.state, setResults]);

  const showPlaceholderChart = loading || !initialized;
  const { submittedData } = data ?? {};

  return (
    <>
      <ReportSection
        backgroundImage={`url('${getStaticImageSrc('salary-bg.svg')}')`}
        chart={
          data === null && !loading ? (
            <NoDataContainer>
              <img
                src={getStaticImageSrc('calculator.svg')}
                height={430}
                width={430}
                alt="calculator"
              />
            </NoDataContainer>
          ) : (
            <MainChart
              // @ts-expect-error TS(2322): Type '{ xTicks: (string | number)[]; lineChart: Li... Remove this comment to see the full error message
              data={showPlaceholderChart ? INITIAL_MAIN_CHART : data}
              setData={setData}
              initialized={initialized}
              loading={loading}
            />
          )
        }
        chartFooter={
          <MainChartFooter
            // @ts-expect-error TS(2322): Type '{ xTicks: (string | number)[]; lineChart: Li... Remove this comment to see the full error message
            data={showPlaceholderChart ? INITIAL_MAIN_CHART : data}
            initialized={initialized}
            loading={loading}
          />
        }
        chartHeader={
          <>
            <div ref={mainChartRef} />
            <MainChartHeader
              // @ts-expect-error TS(2322): Type 'MainChartData | undefined' is not assignable... Remove this comment to see the full error message
              data={data}
              initialized={initialized}
              loading={loading}
            />
          </>
        }
        sidebar={
          <MainChartSidebar
            formAnalyticsTitle="[main] Salary report page - main chart"
            formUsageContext="page"
            setInitialized={setInitialized}
            setLoading={setLoading}
            setResults={setResults}
            setCareerPathData={setCareerPathData}
            searchApiKey={searchApiKey ?? null}
            setSearchApiKey={setSearchApiKey}
          />
        }
      />
      {initialized && data && careerPathData.length > 0 ? (
        <ReportSection
          chartHasBorderTop
          chartHeader={
            <CareerPathChartHeader
              formAnalyticsTitle="[main] Salary report page - career path chart"
              formUsageContext="page"
              careerPathData={careerPathData}
              setCareerPathData={setCareerPathData}
              searchApiKey={searchApiKey ?? null}
              setSearchApiKey={setSearchApiKey}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              city={submittedData.city}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              stateCode={submittedData.stateCode}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              yearsOfExperience={submittedData.yearsOfExperience}
              // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
              salaryModelCreated={salaryModelCreated}
              // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              selectedSynonymId={submittedData.selectedSynonymId}
              analyticsData={{
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                job_title_main: submittedData.jobTitleSelected,
                job_titles_displayed: careerPathData.map((estimatedRange) => ({
                  synonym_name: estimatedRange.synonymName,
                  standard_name: estimatedRange.jobTitleName,
                })),
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                city: submittedData?.city,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                employer_city_bucket: data.filtersUsed?.cityBucket,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                state: data.submittedData?.stateCode,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                years_of_experience: data.submittedData?.yearsOfExperience,
                // @ts-expect-error TS(2322): Type '[number, number] | undefined' is not assigna... Remove this comment to see the full error message
                years_of_experience_used: data.filtersUsed?.yearsOfExperience,
              }}
            />
          }
          chart={
            <CareerPathChart
              analyticsData={{
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                job_title_main: submittedData.jobTitleSelected,
                job_titles_displayed: careerPathData.map((estimatedRange) => ({
                  synonym_name: estimatedRange.synonymName,
                  standard_name: estimatedRange.jobTitleName,
                })),
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                city: submittedData?.city,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                employer_city_bucket: data.filtersUsed?.cityBucket,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                state: data.submittedData?.stateCode,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                years_of_experience: data.submittedData?.yearsOfExperience,
                // @ts-expect-error TS(2322): Type '[number, number] | undefined' is not assigna... Remove this comment to see the full error message
                years_of_experience_used: data.filtersUsed?.yearsOfExperience,
              }}
              // @ts-expect-error TS(2345): Argument of type 'EstimatedRange[]' is not assigna... Remove this comment to see the full error message
              data={buildCareerPathChart(careerPathData)}
              loading={loading}
            />
          }
          header={<CareerPathHeader data={data} />}
        />
      ) : null}
      {initialized && submittedData ? (
        <FeedbackBar submittedData={submittedData} />
      ) : null}
      <RelatedJobs
        headline={initialized ? i18n('Related Jobs') : i18n('Recent Jobs')}
        submittedData={submittedData}
      />
      <RecentSubmissions
        alreadySubmitted={alreadySubmitted}
        surveyLoading={surveyLoading}
      />
    </>
  );
}

function Footer() {
  const { i18n } = useI18n();

  return (
    <Box mx="auto" maxWidth={480}>
      <Text styleAs="bodySmall" mt="1em">
        <Text styleAs="h5" tag="span">
          {
            /* TRANSLATORS: Salary explorer footer */ i18n(
              'We take data security very seriously. ',
            )
          }
        </Text>
        <span>
          {i18n(`The information you enter in the survey will not be linked to your
          user account, and will be completely anonymized. `)}
        </span>
        {/* TRANSLATORS: Salary explorer footer */ i18n('Want to know more? ')}
        {
          /* TRANSLATORS: Salary explorer footer */ i18n(
            `{{link}}Learn more in our FAQ{{/link}} about how we protect your privacy.`,
            {
              link: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={salariesFaqRoute.fullPath}
                />
              ),
            },
          )
        }
      </Text>
    </Box>
  );
}

function LoginHeader() {
  const { i18n } = useI18n();

  return (
    <>
      <Box textAlign="center">
        <IdealistLogo height={82} />
      </Box>
      <H4 mb="0.5em" textAlign="center" mt={1}>
        {i18n('Sign up or log in to see what you could be earning!')}
      </H4>
      <Text styleAs="bodySmall" mb="1em" textAlign="center">
        {i18n('Powered by Idealist’s database of nonprofit salaries.')}
      </Text>
    </>
  );
}

// oxlint-disable-next-line typescript/no-explicit-any
const getSteps = ({ redirectTo, signupMode, setSignupMode }: any) => {
  const stepZero = {
    hideNextStep: true,
    submitIsDisabled: false,
    loadingSection: (
      <>
        <Box mx="auto" pt={36} maxWidth={480}>
          <LoginHeader />
          {signupMode && (
            <EmailSignupFormContainer
              analyticsTitle="[main] Salary report page - signup mode"
              usageContext="page"
              loginOverrideFn={() => {
                setSignupMode(false);
              }}
              redirectTo={redirectTo}
            />
          )}
          {!signupMode && (
            <LoginFormContainer
              analyticsTitle="[main] Salary report page"
              usageContext="page"
              signUpOverrideFn={() => {
                setSignupMode(true);
              }}
              redirectTo={redirectTo}
              email={null}
              inviteId={null}
              initialWarning={null}
            />
          )}
        </Box>
        <Box mt={72}>
          <Footer />
        </Box>
      </>
    ),
    sections: [],
    ignoreInNav: true,
  };
  return [stepZero];
};

function LoginFormWrapper() {
  const [signupMode, setSignupMode] = useState(true);
  const location = useLocation<LocationState>();
  const redirectTo = `${location.pathname}${location.search}`;

  return (
    <Box bg="white" position="relative" zIndex={2}>
      <MultiStepForm
        steps={getSteps({
          redirectTo,
          signupMode,
          setSignupMode,
        })}
        fields={{}}
        hideHeader
        sidebar={[
          {
            imageUrl: getStaticImageSrc('compdb/survey-step1.svg'),
            width: 270,
          },
        ]}
      />
    </Box>
  );
}

export function SalaryReportContainerContainer() {
  return (
    <LoggedInSwitcher loading={<Loading />} loggedOut={<LoginFormWrapper />}>
      <SalaryReportContainer />
    </LoggedInSwitcher>
  );
}
